import type { ApiResponse } from '@lensora/shared'

import { authClient } from './auth.js'

const BASE_URL = import.meta.env['VITE_API_URL'] ?? ''
const DEV_TENANT_SLUG = import.meta.env['VITE_TENANT_SLUG'] as string | undefined

async function getAuthHeader(): Promise<Record<string, string>> {
  const session = await authClient.getSession()
  if (!session) return {}
  return { Authorization: `Bearer ${session.accessToken}` }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const authHeader = await getAuthHeader()

  const res = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...authHeader,
      ...(DEV_TENANT_SLUG ? { 'X-Tenant-Slug': DEV_TENANT_SLUG } : {}),
      ...init.headers,
    },
  })

  const body = (await res.json()) as ApiResponse<T>

  if (!body.success) {
    throw new ApiError(body.error, body.code, res.status)
  }

  return body.data
}

// For asset URLs (e.g. <img src>) that must hit the API host directly —
// relative paths only work in dev where the Vite proxy fronts the API.
export function apiUrl(path: string): string {
  return `${BASE_URL}${path}`
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly code: string | undefined,
    public readonly status: number
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export const api = {
  get: <T>(path: string, init?: RequestInit) =>
    request<T>(path, { ...init, method: 'GET' }),

  post: <T>(path: string, body: unknown, init?: RequestInit) =>
    request<T>(path, {
      ...init,
      method: 'POST',
      body: JSON.stringify(body),
    }),

  patch: <T>(path: string, body: unknown, init?: RequestInit) =>
    request<T>(path, {
      ...init,
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  delete: <T>(path: string, init?: RequestInit) =>
    request<T>(path, { ...init, method: 'DELETE' }),

  // Multipart uploads — never set Content-Type manually so the browser can
  // attach the multipart boundary itself; requestForm skips the default
  // JSON header that `request` applies to every other method.
  postForm: <T>(path: string, formData: FormData) => requestForm<T>(path, formData),

  // Binary downloads (e.g. server-rendered PDFs) — same request shape as
  // `post`, but the success path returns the raw Response so callers can
  // read both the blob body and headers like Content-Disposition.
  postBlob: (path: string, body: unknown, init?: RequestInit) => requestBlob(path, body, init),
}

async function requestBlob(path: string, body: unknown, init: RequestInit = {}): Promise<Response> {
  const authHeader = await getAuthHeader()

  const res = await fetch(`${BASE_URL}${path}`, {
    ...init,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeader,
      ...(DEV_TENANT_SLUG ? { 'X-Tenant-Slug': DEV_TENANT_SLUG } : {}),
      ...init.headers,
    },
    body: JSON.stringify(body),
  })

  if (!res.ok) {
    let errBody: ApiResponse<unknown> | undefined
    try {
      errBody = (await res.json()) as ApiResponse<unknown>
    } catch {
      throw new ApiError(res.statusText || 'Request failed', undefined, res.status)
    }
    if (!errBody.success) {
      throw new ApiError(errBody.error, errBody.code, res.status)
    }
    throw new ApiError('Request failed', undefined, res.status)
  }

  return res
}

async function requestForm<T>(path: string, formData: FormData): Promise<T> {
  const authHeader = await getAuthHeader()

  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    headers: {
      ...authHeader,
      ...(DEV_TENANT_SLUG ? { 'X-Tenant-Slug': DEV_TENANT_SLUG } : {}),
    },
    body: formData,
  })

  const body = (await res.json()) as ApiResponse<T>

  if (!body.success) {
    throw new ApiError(body.error, body.code, res.status)
  }

  return body.data
}
